import { RiCalendarLine } from "@itandi/itandi-bb-ui";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { Deal, DealType } from "~/models/Deal";
import { getWitnessLabel } from "~/models/Deal";
import type { DealParam, RelatedDealParam } from "~/models/DealForm";
import { getNextWitnessType, isWitnessRequired } from "~/models/DealForm";
import type { Leasing } from "~/models/Leasing";
import { formatDate, toDate } from "~/utils/DateUtil";
import Styles from "./Block.module.scss";

const witnessFieldNames = [
	"witnessName",
	"witnessNameKana",
	"witnessTel",
	"witnessEmail"
] as const;

type Props = {
	deal?: Deal;
	dealType: DealType | null;
	leasing: Leasing | null;
};
export function MoveOutBlock({ deal, dealType, leasing }: Props) {
	const {
		register,
		setValue,
		watch,
		formState: { errors },
		clearErrors
	} = useFormContext<DealParam | RelatedDealParam>();
	const terminationDate = watch("terminationDate");
	const inspectionBy = watch("inspectionBy");
	const witnessType = watch("witnessType");
	const inspectionCandidateDate1 = watch("inspectionCandidateDate1");
	const inspectionCandidateDate2 = watch("inspectionCandidateDate2");
	const inspectionCandidateDate3 = watch("inspectionCandidateDate3");
	const isRestorationDealType = dealType === "restoration";
	const witnessLabel = getWitnessLabel(dealType);
	const showWitnessType = !isRestorationDealType || inspectionBy !== "none";
	const witnessRequired = isWitnessRequired({
		dealType,
		inspectionBy,
		witnessType
	});
	const [selectedLeasingTenant, setSelectedLeasingTenant] = useState("");
	const onTenantChange = (e: ChangeEvent<HTMLSelectElement>): void => {
		const val = e.target.value;
		setSelectedLeasingTenant(val);
		if (val !== "" && leasing !== null) {
			const tenant = leasing.leasingTenants.find((t) => t.id === Number(val))!;
			setValue("witnessName", tenant.name, { shouldValidate: true });
			setValue("witnessNameKana", tenant.nameKana, { shouldValidate: true });
			setValue("witnessTel", tenant.tel, { shouldValidate: true });
			setValue("witnessEmail", tenant.email, { shouldValidate: true });
		}
	};
	const onWitnessTypeChange = (value: DealParam["witnessType"]): void => {
		setValue("witnessType", value, { shouldValidate: true });
		if (value !== "specified") {
			clearErrors(witnessFieldNames);
		}
	};
	const onInspectionByChange = (value: DealParam["inspectionBy"]): void => {
		setValue("inspectionBy", value, { shouldValidate: true });
		const nextWitnessType = getNextWitnessType({
			currentWitnessType: witnessType,
			dealType,
			inspectionBy: value
		});
		setValue("witnessType", nextWitnessType, { shouldValidate: true });
		if (nextWitnessType !== "specified") {
			clearErrors(witnessFieldNames);
		}
	};
	useEffect(() => {
		if (deal) {
			setValue("terminationDate", deal.terminationDate);
		} else if (leasing !== null && dealType === "restoration") {
			// leasing の日時情報は退去関連の情報
			setValue("terminationDate", leasing.terminationDate);
			setValue("inspectionCandidateDate1", leasing.inspectionCandidateDate1);
			setValue("inspectionCandidateDate2", leasing.inspectionCandidateDate2);
			setValue("inspectionCandidateDate3", leasing.inspectionCandidateDate3);
			setValue(
				"inspectionCandidateDateTime1",
				leasing.inspectionCandidateTimeRange1
			);
			setValue(
				"inspectionCandidateDateTime2",
				leasing.inspectionCandidateTimeRange2
			);
			setValue(
				"inspectionCandidateDateTime3",
				leasing.inspectionCandidateTimeRange3
			);
			const hasInspectionPersonData = [
				leasing.inspectionPersonName,
				leasing.inspectionPersonNameKana,
				leasing.inspectionPersonPhoneNumber,
				leasing.inspectionPersonEmail
			].some((v) => v !== null && v !== "");
			if (hasInspectionPersonData) {
				// KTから立会人情報が連携された時、witness_typeを「契約者以外」にし連携された人物の情報でフォームを埋める
				// 連携された人物が「契約者」と一致する可能性もあるが、そのあたりのwitness_typeのズレは許容する
				setValue("witnessType", "specified", { shouldValidate: true });
				setValue("witnessName", leasing.inspectionPersonName, {
					shouldValidate: true
				});
				setValue("witnessNameKana", leasing.inspectionPersonNameKana, {
					shouldValidate: true
				});
				setValue("witnessTel", leasing.inspectionPersonPhoneNumber, {
					shouldValidate: true
				});
				setValue("witnessEmail", leasing.inspectionPersonEmail, {
					shouldValidate: true
				});
			}
		}
	}, [deal, dealType, leasing, setValue]);
	return (
		<div className={Styles.Block}>
			<div className={Styles.BlockCaptionWrapper}>
				<div className={Styles.BlockCaption}>
					<span className={Styles.BlockIconWrapper}>
						<RiCalendarLine className={Styles.BlockIcon} />
					</span>
					<span className={Styles.BlockCaptionLabel}>
						{isRestorationDealType ? "退去・立会情報" : "訪問情報"}
					</span>
				</div>
			</div>
			<div className={Styles.BlockForms}>
				{isRestorationDealType ? (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>立会担当会社</span>
							<PresentationChip variant="red">必須</PresentationChip>
						</div>
						<div className={Styles.FormContent}>
							<div className={Styles.InputRadioGroup}>
								<InputRadio
									checked={inspectionBy === "vendor"}
									label="施工会社"
									name="inspectionBy"
									onChange={(): void => {
										onInspectionByChange("vendor");
									}}
									value="vendor"
								/>
								<InputRadio
									checked={inspectionBy === "company"}
									label="管理会社"
									name="inspectionBy"
									onChange={(): void => {
										onInspectionByChange("company");
									}}
									value="company"
								/>
								<InputRadio
									checked={inspectionBy === "none"}
									label="なし"
									name="inspectionBy"
									onChange={(): void => {
										onInspectionByChange("none");
									}}
									value="none"
								/>
							</div>
							{errors.inspectionBy ? (
								<div className={Styles.ErrorMessage}>
									{errors.inspectionBy.message}
								</div>
							) : null}
						</div>
					</div>
				) : null}
				{showWitnessType ? (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>{witnessLabel}</span>
							<PresentationChip variant="red">必須</PresentationChip>
						</div>
						<div className={Styles.FormContent}>
							<div className={Styles.InputRadioGroup}>
								<InputRadio
									checked={witnessType === "tenant"}
									label="契約者"
									name="witnessType"
									onChange={(): void => {
										onWitnessTypeChange("tenant");
									}}
									value="tenant"
								/>
								<InputRadio
									checked={witnessType === "specified"}
									label="契約者以外"
									name="witnessType"
									onChange={(): void => {
										onWitnessTypeChange("specified");
									}}
									value="specified"
								/>
								{!isRestorationDealType ? (
									<InputRadio
										checked={witnessType === "none"}
										label="なし"
										name="witnessType"
										onChange={(): void => {
											onWitnessTypeChange("none");
										}}
										value="none"
									/>
								) : null}
							</div>
							{errors.witnessType ? (
								<div className={Styles.ErrorMessage}>
									{errors.witnessType.message}
								</div>
							) : null}
						</div>
					</div>
				) : null}
				{witnessType === "specified" ? (
					<>
						{leasing !== null && leasing.leasingTenants.length > 0 ? (
							<div className={Styles.FormRow}>
								<div className={Styles.FormLabel}>
									<span className={Styles.FormLabelText}>
										{`${witnessLabel}を入居者から選択`}
									</span>
								</div>
								<div className={Styles.FormContent}>
									<div className={Styles.InputWrapper}>
										<InputSelect
											onChange={onTenantChange}
											value={selectedLeasingTenant}
										>
											<option value="">選択してください</option>
											{leasing.leasingTenants.map((tenant, i) => (
												<option
													key={String(tenant.id)}
													value={String(tenant.id)}
												>
													{`入居者${i + 1}：${tenant.name ?? ""}`}
												</option>
											))}
										</InputSelect>
									</div>
								</div>
							</div>
						) : null}
						<div className={Styles.FormRow}>
							<div className={Styles.FormLabel}>
								<span className={Styles.FormLabelText}>
									{witnessLabel} 氏名
								</span>
								{witnessRequired ? (
									<PresentationChip variant="red">必須</PresentationChip>
								) : null}
							</div>
							<div className={Styles.FormContent}>
								<div className={Styles.NameKanaGroup}>
									<div className={Styles.NameInputWrapper}>
										<Input
											isInvalid={!!errors.witnessName}
											placeholder="例）内装 太郎"
											{...register("witnessName")}
										/>
									</div>
									{errors.witnessName ? (
										<div className={Styles.ErrorMessage}>
											{errors.witnessName.message}
										</div>
									) : null}
									<div className={Styles.NameInputLabel}>カナ</div>
									<div>
										<Input
											isInvalid={!!errors.witnessNameKana}
											placeholder="例）ナイソウ タロウ"
											{...register("witnessNameKana")}
										/>
									</div>
									{errors.witnessNameKana ? (
										<div className={Styles.ErrorMessage}>
											{errors.witnessNameKana.message}
										</div>
									) : null}
								</div>
							</div>
						</div>
						<div className={Styles.FormRow}>
							<div className={Styles.FormLabel}>
								<span className={Styles.FormLabelText}>
									{witnessLabel} 電話番号
								</span>
								{witnessRequired ? (
									<PresentationChip variant="red">必須</PresentationChip>
								) : null}
							</div>
							<div className={Styles.FormContent}>
								<div className={Styles.InputWrapper}>
									<Input
										isInvalid={!!errors.witnessTel}
										placeholder="例）090-0000-0000"
										{...register("witnessTel")}
									/>
								</div>
								{errors.witnessTel ? (
									<div className={Styles.ErrorMessage}>
										{errors.witnessTel.message}
									</div>
								) : null}
							</div>
						</div>
						<div className={Styles.FormRow}>
							<div className={Styles.FormLabel}>
								<span className={Styles.FormLabelText}>
									{witnessLabel} メールアドレス
								</span>
							</div>
							<div className={Styles.FormContent}>
								<div className={Styles.InputWrapper}>
									<Input
										isInvalid={!!errors.witnessEmail}
										placeholder="例）sample@example.com"
										{...register("witnessEmail")}
									/>
								</div>
								{errors.witnessEmail ? (
									<div className={Styles.ErrorMessage}>
										{errors.witnessEmail.message}
									</div>
								) : null}
							</div>
						</div>
					</>
				) : null}
				{witnessType !== "none" ? (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>日程調整時の注意事項</span>
						</div>
						<div className={Styles.FormContent}>
							<div className={Styles.InputWrapper}>
								<InputText
									isInvalid={!!errors.inspectionScheduleAdjustmentNote}
									rows={5}
									{...register("inspectionScheduleAdjustmentNote")}
									placeholder={
										isRestorationDealType
											? "例）当日の立会人ではなく、契約者の田中様（03-1234-5678）へご連絡ください。"
											: "例）平日18時以降に連絡してください。"
									}
								/>
							</div>
							{errors.inspectionScheduleAdjustmentNote ? (
								<div className={Styles.ErrorMessage}>
									{errors.inspectionScheduleAdjustmentNote.message}
								</div>
							) : null}
							<p className={Styles.Description}>
								{`${witnessLabel}以外へ日程調整の連絡が必要な場合や、連絡可能時間などがあれば入力してください。`}
							</p>
						</div>
					</div>
				) : null}
				{isRestorationDealType ? (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>解約日</span>
						</div>
						<div className={Styles.FormContent}>
							<InputDatev2
								isInvalid={false}
								maxWidth="13rem"
								onChange={(date): void => {
									setValue("terminationDate", formatDate(date));
								}}
								value={toDate(terminationDate)}
							/>
						</div>
					</div>
				) : null}
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>訪問希望日1</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<InputDatev2
									isInvalid={false}
									maxWidth="13rem"
									onChange={(date): void => {
										setValue("inspectionCandidateDate1", formatDate(date));
									}}
									value={toDate(inspectionCandidateDate1)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）午前中"
									{...register("inspectionCandidateDateTime1")}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>訪問希望日2</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<InputDatev2
									isInvalid={false}
									maxWidth="13rem"
									onChange={(date): void => {
										setValue("inspectionCandidateDate2", formatDate(date));
									}}
									value={toDate(inspectionCandidateDate2)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）12時-14時"
									{...register("inspectionCandidateDateTime2")}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>訪問希望日3</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<InputDatev2
									isInvalid={false}
									maxWidth="13rem"
									onChange={(date): void => {
										setValue("inspectionCandidateDate3", formatDate(date));
									}}
									value={toDate(inspectionCandidateDate3)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）午後"
									{...register("inspectionCandidateDateTime3")}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
